import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import PaymentBadges from "@/components/PaymentBadges";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ChevronLeft, Lock, Loader2 } from "lucide-react";

const appearance = {
  theme: "night",
  variables: {
    colorPrimary: "#f5f5f7", colorBackground: "#0f0f10", colorText: "#f5f5f7",
    colorTextSecondary: "#a1a1aa", colorDanger: "#ef4444", fontFamily: "Space Grotesk, sans-serif",
    borderRadius: "0px", spacingUnit: "4px",
  },
  rules: {
    ".Input": { border: "1px solid rgba(255,255,255,0.2)", padding: "12px" },
    ".Input:focus": { border: "1px solid rgba(255,255,255,0.6)", boxShadow: "none" },
    ".Label": { textTransform: "uppercase", letterSpacing: "0.15em", fontSize: "10px", color: "#a1a1aa" },
  },
};

const PayForm = ({ amountLabel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/payment/success` },
    });
    if (error) {
      toast.error(error.message || "Payment could not be completed");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="stripe-payment-form">
      <PaymentElement
        onReady={() => setReady(true)}
        options={{
          layout: "tabs",
          wallets: { applePay: "auto", googlePay: "never" },
        }}
      />
      <button
        data-testid="pay-now-btn"
        type="submit"
        disabled={!stripe || !ready || submitting}
        className="w-full mt-8 bg-white text-black py-4 font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-transparent hover:text-white border border-white disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 className="animate-spin" size={15} /> : <Lock size={13} strokeWidth={1.5} />}
        {submitting ? "Processing…" : `Pay ${amountLabel}`}
      </button>
      <p className="mt-4 text-center font-mono-label text-[10px] uppercase tracking-luxe text-white/30">
        Test card 4242 4242 4242 4242 · any future date · any CVC
      </p>
      <PaymentBadges className="mt-4" />
    </form>
  );
};

export default function Checkout() {
  const { items } = useCart();
  const { currency, symbol, convert, formatValue } = useCurrency();
  const navigate = useNavigate();

  const [step, setStep] = useState("details"); // details | payment
  const [details, setDetails] = useState({
    name: "", email: "", line1: "", line2: "", city: "", postal_code: "",
    country: "CH",
  });
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [promo, setPromo] = useState("");
  const [discount, setDiscount] = useState(null); // {code, amount, label}

  useEffect(() => {
    if (items.length === 0) navigate("/cart");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Currency changed: drop the PaymentIntent and applied code so totals are rebuilt in the new currency.
  useEffect(() => {
    setStep("details");
    setClientSecret("");
    setTotals(null);
    setDiscount(null);
  }, [currency]);

  const subtotal = items.reduce((s, i) => s + convert(i.price) * i.quantity, 0);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try {
      const { data } = await api.post("/discounts/validate", { code: promo, currency, subtotal });
      if (data.valid) {
        setDiscount({ code: data.code, amount: data.amount, label: data.label });
        toast.success(`Code applied — ${data.label}`);
      } else {
        setDiscount(null);
        toast.error(data.reason === "min_not_met" ? "Order too small for this code" : "Invalid code");
      }
    } catch (e) {
      setDiscount(null);
      toast.error("Could not apply code");
    }
  };

  const startPayment = async (e) => {
    e.preventDefault();
    if (!details.email || !details.name || !details.line1 || !details.city || !details.postal_code || !details.country) {
      toast.error("Please complete your contact and shipping details");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })),
        currency,
        customer: { name: details.name, email: details.email },
        shipping_address: {
          line1: details.line1, line2: details.line2, city: details.city,
          postal_code: details.postal_code, country: details.country,
        },
        discount_code: discount?.code || null,
      };
      const { data } = await api.post("/payments/create-intent", payload);
      setStripePromise(loadStripe(data.publishable_key));
      setClientSecret(data.client_secret);
      setTotals(data);
      setStep("payment");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start checkout");
      toast.error(err.response?.data?.detail || "Could not start checkout");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-white/60 text-sm";
  const label = "font-mono-label text-[10px] uppercase tracking-luxe text-white/40 mb-2 block";

  const displaySubtotal = totals ? totals.subtotal : subtotal;
  const displayShipping = totals ? totals.shipping_fee : null;
  const displayDiscount = totals ? (totals.discount_amount || 0) : (discount ? discount.amount : 0);
  const displayTotal = totals ? totals.amount : Math.max(0, subtotal - displayDiscount);

  return (
    <div className="pt-28 lg:pt-36 pb-24 max-w-[1200px] mx-auto px-6 lg:px-12">
      <button
        data-testid="checkout-back-btn"
        onClick={() => (step === "payment" ? setStep("details") : navigate("/cart"))}
        className="flex items-center gap-1 font-mono-label text-xs uppercase tracking-luxe text-white/50 hover:text-white mb-8"
      >
        <ChevronLeft size={14} /> {step === "payment" ? "Edit details" : "Back to Bag"}
      </button>
      <h1 className="font-serif-display text-5xl lg:text-6xl font-light uppercase tracking-tight mb-4">Checkout</h1>
      <p className="font-mono-label text-[10px] uppercase tracking-luxe text-white/40 mb-12">
        Guest checkout · Paying in {currency.toUpperCase()}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
        <div className="order-2 lg:order-1">
          {step === "details" && (
            <form onSubmit={startPayment} data-testid="checkout-details-form" className="space-y-5">
              <h2 className="font-mono-label text-xs uppercase tracking-luxe text-white/50 mb-2">Contact</h2>
              <div>
                <label className={label}>Email</label>
                <input data-testid="co-email" type="email" required value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} className={field} placeholder="you@email.com" />
              </div>
              <div>
                <label className={label}>Full name</label>
                <input data-testid="co-name" required value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} className={field} placeholder="First and last name" />
              </div>
              <h2 className="font-mono-label text-xs uppercase tracking-luxe text-white/50 pt-4 mb-2">Shipping address</h2>
              <div>
                <label className={label}>Address</label>
                <input data-testid="co-line1" required value={details.line1} onChange={(e) => setDetails({ ...details, line1: e.target.value })} className={field} placeholder="Street and number" />
              </div>
              <input data-testid="co-line2" value={details.line2} onChange={(e) => setDetails({ ...details, line2: e.target.value })} className={field} placeholder="Apartment, suite (optional)" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={label}>Postal code</label>
                  <input data-testid="co-postal" required value={details.postal_code} onChange={(e) => setDetails({ ...details, postal_code: e.target.value })} className={field} />
                </div>
                <div>
                  <label className={label}>City</label>
                  <input data-testid="co-city" required value={details.city} onChange={(e) => setDetails({ ...details, city: e.target.value })} className={field} />
                </div>
              </div>
              <div>
                <label className={label}>Country</label>
                <input data-testid="co-country" required readOnly value={details.country} title="We currently ship within Switzerland only" className={field} />
              </div>
              <div className="pt-2">
                <label className={label}>Promo code</label>
                <div className="flex gap-2">
                  <input data-testid="co-promo" value={promo} onChange={(e) => setPromo(e.target.value)} className={field} placeholder="Enter code" />
                  <button type="button" data-testid="co-apply-promo" onClick={applyPromo} className="px-6 shrink-0 border border-white/25 text-white/80 font-mono-label text-xs uppercase tracking-luxe hover:border-white">
                    Apply
                  </button>
                </div>
                {discount && <p className="mt-2 font-mono-label text-[10px] uppercase tracking-luxe text-green-400">{discount.code} · {discount.label}</p>}
              </div>
              {error && <p data-testid="checkout-error" className="text-red-300 text-sm">{error}</p>}
              <button
                data-testid="continue-to-payment-btn"
                type="submit"
                disabled={loading}
                className="w-full mt-2 bg-white text-black py-4 font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-transparent hover:text-white border border-white disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" size={15} /> : null}
                {loading ? "Preparing…" : "Continue to Payment"}
              </button>
              <PaymentBadges className="mt-4" />
            </form>
          )}

          {step === "payment" && clientSecret && stripePromise && (
            <div>
              <h2 className="font-mono-label text-xs uppercase tracking-luxe text-white/50 mb-8">Payment</h2>
              <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
                <PayForm amountLabel={`${symbol}${(displayTotal).toLocaleString("en-US")}`} />
              </Elements>
            </div>
          )}
        </div>

        <div className="order-1 lg:order-2 lg:sticky lg:top-28 lg:self-start border border-white/10 p-8 h-fit">
          <h2 className="font-mono-label text-xs uppercase tracking-luxe text-white/50 mb-8">Order Summary</h2>
          <div className="space-y-5 mb-8">
            {items.map((i) => (
              <div key={`${i.product_id}-${i.size}`} className="flex gap-4">
                <div className="w-16 h-20 bg-neutral-900 shrink-0 overflow-hidden">
                  {i.image && <img src={mediaUrl(i.image)} alt={i.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  <p className="text-white text-sm">{i.name}</p>
                  <p className="font-mono-label text-xs text-white/40 mt-1 uppercase tracking-luxe">Size {i.size} · Qty {i.quantity}</p>
                </div>
                <span className="font-mono-label text-sm text-white/80 self-center">{formatValue(convert(i.price) * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 pt-6 border-t border-white/10 text-sm">
            <div className="flex justify-between text-white/70"><span>Subtotal</span><span className="font-mono-label">{formatValue(displaySubtotal)}</span></div>
            {displayDiscount > 0 && (
              <div className="flex justify-between text-green-400"><span>Discount{totals?.discount_code || discount?.code ? ` (${totals?.discount_code || discount?.code})` : ""}</span><span data-testid="checkout-discount" className="font-mono-label">-{formatValue(displayDiscount)}</span></div>
            )}
            <div className="flex justify-between text-white/70">
              <span>Shipping</span>
              <span data-testid="checkout-shipping" className="font-mono-label">
                {displayShipping === null ? "Calculated next" : displayShipping === 0 ? "Free" : formatValue(displayShipping)}
              </span>
            </div>
          </div>
          <div className="flex justify-between text-white text-lg mt-4 pt-4 border-t border-white/10">
            <span className="font-mono-label text-xs uppercase tracking-luxe self-center text-white/60">Total</span>
            <span data-testid="checkout-total" className="font-mono-label">{formatValue(displayTotal)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
